"use client";

import { useEffect, useRef, useState } from "react";
import { getHealth, healthEventSource } from "./api";
import type { GpuHealth } from "./types";

export interface ServiceHealthState {
  health: GpuHealth | null;
  error: string | null;
  isReady: (modelId: string) => boolean;
}

/**
 * Subscribes to /v1/health/stream. The stream auto-closes when status === "ok"
 * (per TDD-0015 §Interface Design); this hook reconnects if the GPU service
 * regresses to "starting" (e.g. after a model unload from the idle sweeper).
 */
export function useServiceHealth(): ServiceHealthState {
  const [health, setHealth] = useState<GpuHealth | null>(null);
  const [error, setError] = useState<string | null>(null);
  const closedByServerRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    let es: EventSource | null = null;
    let slowPollTimer: ReturnType<typeof setTimeout> | null = null;

    const connect = () => {
      if (cancelled) return;
      es = healthEventSource();
      es.onmessage = (evt) => {
        try {
          const parsed = JSON.parse(evt.data) as GpuHealth;
          setHealth(parsed);
          setError(null);
          if (parsed.status === "ok") {
            // Server will close on its own; mark so we don't auto-reconnect.
            closedByServerRef.current = true;
            es?.close();
            startSlowPoll();
          }
        } catch (e) {
          console.warn("[health] parse error", e);
        }
      };
      es.onerror = () => {
        // EventSource auto-reconnects on transient errors. We only intervene
        // if the server intentionally closed with status:"ok".
        if (closedByServerRef.current) {
          es?.close();
        }
      };
    };

    const startSlowPoll = () => {
      // Once status is "ok", a slow background poll catches regressions
      // (e.g. idle sweeper unloads a model). 60s is generous; the FE just
      // wants to know if it should re-show "loading" before a generate.
      const tick = async () => {
        if (cancelled) return;
        try {
          const next = await getHealth();
          setHealth(next);
          if (next.status !== "ok") {
            closedByServerRef.current = false;
            connect();
            return;
          }
        } catch (e) {
          setError((e as Error).message);
        }
        slowPollTimer = setTimeout(tick, 60_000);
      };
      slowPollTimer = setTimeout(tick, 60_000);
    };

    connect();

    return () => {
      cancelled = true;
      if (slowPollTimer) clearTimeout(slowPollTimer);
      es?.close();
    };
  }, []);

  return {
    health,
    error,
    isReady: (modelId: string) => Boolean(health?.models?.[modelId]?.loaded),
  };
}
